import { Icon } from '@/shared/assets/icons';
import { cn } from '@/shared/lib/cn';
import type { FC } from 'react';

/** Figma node 248:20078 draws the tab 136px tall. */
export const EDGE_TAB_HEIGHT = 136;

type AiInsightEdgeTabProps = {
  isOpen: boolean;
  onClick: () => void;
  /**
   * Distance from the panel's top to the tab's top, in px — the header's height
   * plus the gap the design leaves under it. See the note on positioning below.
   */
  topOffset: number;
};

/**
 * The vertical "AI Insight" tab. Figma node 248:20078 — 31 x {@link EDGE_TAB_HEIGHT}.
 *
 * One control in two positions, not two components: collapsed it sits at the
 * right edge and expands the panel; expanded it rides the panel's left edge and
 * collapses it. Only the chevron direction changes.
 *
 * Positioned against the panel itself — `absolute` inside the panel's sticky
 * box, pushed one tab-width to the left of it and down by `topOffset`, which is
 * the header's height plus the gap the design leaves under it, so the tab sits
 * level with the summary paragraph (Figma node 244:19890).
 *
 * It has been positioned two other ways, both of which detached it from the
 * panel once the page scrolled far enough:
 *
 * - `fixed` anchors to the viewport while the panel anchors to the document, so
 *   they separate immediately.
 * - `sticky` at a matching offset survives a short scroll but not a long one:
 *   sticky releases when an element's bottom meets its container's, and a 136px
 *   tab stays pinned long after an 872px panel has released.
 *
 * Being a child of the panel is the only version that cannot come apart.
 */
export const AiInsightEdgeTab: FC<AiInsightEdgeTabProps> = ({
  isOpen,
  onClick,
  topOffset,
}) => (
  <button
    type="button"
    data-testid="ai-insight-edge-tab"
    aria-expanded={isOpen}
    style={{ top: topOffset, height: EDGE_TAB_HEIGHT }}
    aria-label={isOpen ? 'Collapse AI Insight' : 'Expand AI Insight'}
    className={cn(
      'bg-primary-6 hover:bg-primary-7 text-primary-inverse',
      'absolute left-0 z-30 w-[31px] -translate-x-full',
      'flex cursor-pointer flex-col items-center justify-center gap-2',
      'rounded-l-lg transition-colors'
    )}
    onClick={onClick}
  >
    <Icon name="ai-stars" size={14} />
    {/* Bottom-to-top so the label reads upward, as drawn. */}
    <span className="[transform:rotate(180deg)] text-xs [writing-mode:vertical-rl]">
      AI Insight
    </span>
    <Icon
      name="chevron-left"
      size={10}
      className={cn('transition-transform', isOpen && 'rotate-180')}
    />
  </button>
);
